import { useEffect, useMemo, useRef, useState } from 'react';

import { Icon, Typography } from '@repo/ui';
import { getAgeGroup } from '@repo/utils';
import { AudienceFeedbackOverlay, ImageFallback } from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { MetricVideoEventProps } from 'contexts/analytics/types';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { InsightMetric } from 'types/Insights';

import {
  getMetricId,
  getYouTubeThumbnails,
  MetricVideo as MetricVideoConfig,
} from './metricVideos';
import MetricVideoPlayer from './MetricVideoPlayer';
import { WatchSummary } from './watchTimer';
import styles from './MetricVideo.module.css';

const SPECIALIST = 'Danielle Shea Tan, MS, CNS, LDN';
// One type for every video, so the prompt is asked once for the whole feature.
export const METRIC_VIDEO_FEEDBACK_TYPE = 'metric_video';

interface MetricVideoProps {
  metric: Partial<InsightMetric>;
  video: MetricVideoConfig;
}

export const MetricVideo = ({ metric, video }: MetricVideoProps) => {
  const { trackEvent } = useAnalytics();
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const hasPromptedFeedback = useRef(false);

  const metricId = getMetricId(metric) ?? 0;
  const thumbnails = getYouTubeThumbnails(video.youtubeId);

  const eventProps = useMemo<MetricVideoEventProps>(
    () => ({
      metric_id: metricId,
      metric_name: metric.name ?? '',
      video_id: video.youtubeId,
      evaluation_color: metric.evaluation_color ?? '',
      evaluation_text: metric.evaluation_text ?? '',
      age_group:
        currentKit?.age_at_sampling != null
          ? getAgeGroup(currentKit.age_at_sampling)
          : undefined,
    }),
    [metricId, metric, video.youtubeId, currentKit?.age_at_sampling],
  );

  // Once per visit: keyed on kit + metric only, so re-renders that recreate
  // eventProps or trackEvent don't fire it again.
  const trackImpression = useRef(() => {});
  trackImpression.current = () =>
    trackEvent('Metric Video Thumbnail Viewed', eventProps);
  useEffect(() => {
    trackImpression.current();
  }, [currentKitId, metricId]);

  const openPlayer = () => {
    trackEvent('Metric Video Played', eventProps);
    setIsFeedbackOpen(false);
    setIsPlayerOpen(true);
  };

  const closePlayer = (summary: WatchSummary) => {
    trackEvent('Metric Video Closed', { ...eventProps, ...summary });
    setIsPlayerOpen(false);
    if (!hasPromptedFeedback.current) {
      hasPromptedFeedback.current = true;
      setIsFeedbackOpen(true);
    }
  };

  return (
    <>
      <button
        type='button'
        className={styles.videoCard}
        onClick={openPlayer}
        aria-label={`Play video: Learn why this matters, with ${SPECIALIST}`}
      >
        <span className={styles.thumbnail}>
          <ImageFallback
            src={thumbnails.vertical}
            fallback={thumbnails.fallback}
            alt=''
            className={styles.thumbnailImage}
          />
          <span className={styles.playButton} aria-hidden='true'>
            <Icon name='playFilled' size='s' />
          </span>
        </span>
        <span className={styles.texts}>
          <Typography variant='body-s'>Learn why this matters.</Typography>
          <Typography variant='label' color='grey'>
            {`with Microbiome specialist, ${SPECIALIST}`}
          </Typography>
        </span>
      </button>

      <MetricVideoPlayer
        youtubeId={video.youtubeId}
        isOpen={isPlayerOpen}
        onClose={closePlayer}
      />

      {!!currentKitId && (
        <AudienceFeedbackOverlay
          kitId={currentKitId}
          feedbackType={METRIC_VIDEO_FEEDBACK_TYPE}
          questions={['VideoHelpfulQuestion', 'VideoTextQuestion']}
          isOpen={isFeedbackOpen}
          onOpenChange={setIsFeedbackOpen}
          ignoreNpsAnswers
          extraInfo={{
            gut_vaginal_health_id: metricId,
            youtube_id: video.youtubeId,
            metric_name: metric.name ?? null,
          }}
        />
      )}
    </>
  );
};

export default MetricVideo;
